import React, { useRef } from "react";
import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";

import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { hasMealPlan } from "../../../../utils/nutrition-progress";
import { resolveMealPlanTitle } from "../../../../utils/meal-planner-helper";

const pg = (t, key, opts) => t(`nutritionPage.pdfGuides.${key}`, opts);

// Same upload target Profile → Guides posts to (Guides.js / UploadGuide.js)
// — same field name ("meal-file") and body shape, so the backend handles it
// identically no matter which screen triggered it. Deliberately a plain
// native <form>, not the `submit-form` JS helper: that helper posts a JSON-
// shaped body and can't carry a real file, only a real multipart form can.
//
// A real <button> (not a styled <label>) triggering the hidden file input
// via ref — gets [data-button-type]'s gradient/hover/focus states for free
// (that system only targets real <button>/[role="button"] elements), the
// same as ExistingPlans.js's own "Create New" trigger, with none of the
// focusability problems a <label> or <span role="button"> would need to
// re-derive by hand.
const UploadForm = ({ athleteId, coachId, buttonType, hasIcon, fieldName, children }) => {
  const fileInputRef = useRef(null);
  return (
    <form
      encType="multipart/form-data"
      method="POST"
      action="/actions/guides/save"
      className="np-pdf-guides__upload-form"
    >
      <input type="hidden" name="athleteId" value={athleteId} />
      {coachId && <input type="hidden" name="userToRequest" value={coachId} />}
      <button
        type="button"
        data-button-type={buttonType}
        data-icon={hasIcon || undefined}
        onClick={() => fileInputRef.current?.click()}
      >
        {children}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        name={fieldName}
        accept=".pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          if (!e.target.files?.[0]) return;
          e.target.form.requestSubmit();
        }}
      />
    </form>
  );
};

UploadForm.propTypes = {
  athleteId: PropTypes.string,
  coachId: PropTypes.string,
  buttonType: PropTypes.oneOf(["primary", "secondary"]),
  hasIcon: PropTypes.bool,
  fieldName: PropTypes.oneOf(["meal-file", "supplement-file"]).isRequired,
  children: PropTypes.node,
};

const PdfGuidesCard = ({ athlete, activePlan }) => {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useRillContext();

  // guides.meal lives on the COACH's own athletes[] roster entry for this
  // athlete, not on the athlete's own user document — confirmed against the
  // save/delete handlers (msb-api guides.js) and mobile's own
  // setProfileGuides(coach, userId), which reads it the same way. athlete
  // itself only ever carries name/dates/etc — asking it directly for
  // .guides (as Profile's own UploadGuide.js does) returns undefined.
  const coach = locals.activeUserToRequest;
  const athleteEntry = coach?.athletes?.find(
    (a) => String(a._id) === String(athlete?._id)
  );
  const mealGuide = athleteEntry?.guides?.meal;
  const supplementGuide = athleteEntry?.guides?.supplement;
  const hasMealPdf = Boolean(mealGuide?.s3Path);
  const hasSupplementPdf = Boolean(supplementGuide?.s3Path);

  const conflict = hasMealPdf && hasMealPlan(activePlan);
  const activeMealPlanTitle = conflict
    ? resolveMealPlanTitle(activePlan.mealPlan, t)
    : null;

  const guidesHref = `/athlete-portal/profile?section=guides&athlete=${athlete?._id}`;

  return (
    <div className="np-pdf-guides">
      <div className="np-pdf-guides__header">
        <div>
          <p className="np-pdf-guides__title">{pg(t, "title")}</p>
          <p className="np-pdf-guides__subtitle">{pg(t, "subtitle")}</p>
        </div>
        <a className="np-pdf-guides__manage-link" href={guidesHref}>
          {pg(t, hasMealPdf || hasSupplementPdf ? "manageInGuides" : "goToGuides")}
          <SvgIcon src="/img/new-icons/arrow-forward.svg" useCurrentColor />
        </a>
      </div>

      {hasMealPdf ? (
        <div className="np-pdf-guides__file">
          <SvgIcon
            src="/img/widget-icons/pdf-alt.svg"
            useCurrentColor
            classNames="np-pdf-guides__file-icon"
          />
          <div className="np-pdf-guides__file-info">
            <span className="np-pdf-guides__file-kicker">{t("nutritionPage.existingPlans.mealPlan")}</span>
            <span className="np-pdf-guides__file-name">{mealGuide.displayName}</span>
          </div>
          <div className="np-pdf-guides__file-actions">
            <a //NOSONAR
              role="button"
              data-button-type="secondary"
              href={mealGuide.s3Path}
              target="_blank"
              rel="noreferrer"
            >
              {pg(t, "view")}
            </a>
            <UploadForm athleteId={athlete?._id} coachId={coach?._id} buttonType="secondary" fieldName="meal-file">
              {pg(t, "replace")}
            </UploadForm>
          </div>
        </div>
      ) : (
        <div className="np-pdf-guides__empty">
          <div className="np-pdf-guides__empty-icon-wrap">
            <SvgIcon src="/img/new-icons/add.svg" useCurrentColor classNames="np-pdf-guides__empty-icon" />
          </div>
          <p className="np-pdf-guides__empty-title">{pg(t, "emptyTitle")}</p>
          <p className="np-pdf-guides__empty-desc">
            {pg(t, "emptyDescription", { name: athlete?.name })}
          </p>
          <UploadForm athleteId={athlete?._id} coachId={coach?._id} buttonType="primary" hasIcon fieldName="meal-file">
            <img src="/img/new-icons/add.svg" alt="" />
            {pg(t, "uploadPdf")}
          </UploadForm>
        </div>
      )}

      {hasSupplementPdf ? (
        <div className="np-pdf-guides__file">
          <SvgIcon
            src="/img/widget-icons/pdf-alt.svg"
            useCurrentColor
            classNames="np-pdf-guides__file-icon"
          />
          <div className="np-pdf-guides__file-info">
            <span className="np-pdf-guides__file-kicker">{pg(t, "supplementGuideLabel")}</span>
            <span className="np-pdf-guides__file-name">{supplementGuide.displayName}</span>
          </div>
          <div className="np-pdf-guides__file-actions">
            <a //NOSONAR
              role="button"
              data-button-type="secondary"
              href={supplementGuide.s3Path}
              target="_blank"
              rel="noreferrer"
            >
              {pg(t, "view")}
            </a>
            <UploadForm
              athleteId={athlete?._id}
              coachId={coach?._id}
              buttonType="secondary"
              fieldName="supplement-file"
            >
              {pg(t, "replace")}
            </UploadForm>
          </div>
        </div>
      ) : (
        <div className="np-pdf-guides__supplement-empty">
          <div className="np-pdf-guides__file-info">
            <span className="np-pdf-guides__file-kicker">{pg(t, "supplementGuideLabel")}</span>
            <span className="np-pdf-guides__empty-desc">{pg(t, "noneUploaded")}</span>
          </div>
          <UploadForm athleteId={athlete?._id} coachId={coach?._id} buttonType="secondary" fieldName="supplement-file">
            {pg(t, "upload")}
          </UploadForm>
        </div>
      )}

      {conflict && (
        <div className="np-pdf-guides__conflict">
          <SvgIcon src="/img/svg/exclamation-circle.svg" useCurrentColor />
          <span>
            <Trans
              t={t}
              i18nKey="nutritionPage.pdfGuides.conflictWarning"
              values={{ name: athlete?.name, mealPlanTitle: activeMealPlanTitle }}
              components={{ strong: <strong className="np-pdf-guides__conflict-title" /> }}
            />
          </span>
        </div>
      )}

      {!(hasMealPdf && hasSupplementPdf) && (
        <p className="np-pdf-guides__footnote">
          <SvgIcon src="/img/new-icons/info-circle.svg" useCurrentColor />
          {pg(t, "sameFileNote")}
        </p>
      )}
    </div>
  );
};

PdfGuidesCard.propTypes = {
  athlete: PropTypes.object,
  activePlan: PropTypes.object,
};

export default PdfGuidesCard;
